import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './PartnersPage.css';

const HERO_IMG = 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop';

const CALENDLY_URL = 'https://calendly.com/admin-ovalcare/30min';

const howHeardOptions = [
  { value: 'representative', label: 'Representative' },
  { value: 'conference', label: 'Conference' },
  { value: 'online', label: 'Online' },
  { value: 'referral', label: 'Referral' },
];

const representativeNames = [
  'Dan Fletcher', 'Daphyne Lovejoy', 'Dee Hohenberger', 'Denise Sherwood',
  'House', 'Jami Lovejoy', 'James Bosshart', 'Kenny D. West', 'Mike Cardoso', 'Sara Murely', 'Vincent Kleszcz',
];

const pillars = [
  { icon: 'bi-phone', title: 'Telehealth Access', desc: 'Members connect with board-certified providers 24/7. Real consultations, real prescriptions.' },
  { icon: 'bi-cash-stack', title: 'Rx at True Cost', desc: 'Up to 80% savings on GLP-1s, mental health, ED, hair loss, testosterone, TRT, peptides and more through licensed pharmacies.' },
  { icon: 'bi-graph-up', title: 'Engagement Analytics', desc: 'Track member utilization, engagement rates, and plan ROI through your partner dashboard.' },
  { icon: 'bi-shield-check', title: 'Compliance Built-In', desc: 'HIPAA-compliant platform, legit-script certification, state-licensed providers, FDA-regulated 503A pharmacies.' },
];

const steps = [
  { num: '01', title: 'Partner Application', desc: 'Quick form, no commitment. We review and reach out within 24 hours.', icon: 'clipboard' },
  { num: '02', title: 'Onboarding Call', desc: '15-minute session with your success manager. Get portal access and co-branded materials.', icon: 'call' },
  { num: '03', title: 'Launch & Grow', desc: 'Roll out to members. Track engagement in real-time. Dedicated support ongoing.', icon: 'rocket' },
];

const offerCategories = [
  { icon: 'bi-lightning-charge', label: 'GLP-1 Weight Loss' },
  { icon: 'bi-emoji-smile', label: 'Mental Health' },
  { icon: 'bi-heart', label: 'Erectile Dysfunction' },
  { icon: 'bi-scissors', label: 'Hair Loss' },
  { icon: 'bi-droplet', label: 'Skin Care' },
  { icon: 'bi-moon', label: 'Sleep & Anxiety' },
  { icon: 'bi-arrow-up-circle', label: 'Testosterone' },
  { icon: 'bi-speedometer2', label: 'TRT' },
  { icon: 'bi-bullseye', label: 'Peptides' },
];

const agentOptions = [
  { value: '1-10', label: '1\u201310 agents' },
  { value: '11-25', label: '11\u201325 agents' },
  { value: '26-50', label: '26\u201350 agents' },
  { value: '51-100', label: '51\u2013100 agents' },
  { value: '100+', label: '100+ agents' },
];

const enrollmentOptions = [
  { value: 'individual', label: 'Individual' },
  { value: 'group', label: 'Group' },
  { value: 'both', label: 'Both' },
];

const quoteWords = [
  'Healthcare', 'should', 'feel', 'like', 'a', 'benefit', 'members',
  'choose', 'to', 'use', '\u2014not', 'a', 'form', 'they', 'dread', 'filling', 'out.'
];

const PartnersPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [calendlyFailed, setCalendlyFailed] = useState(false);
  const [errors, setErrors] = useState({});
  const [metricValues, setMetricValues] = useState({ engagement: 0, savings: 0, states: 0 });
  const metricsCounted = useRef(false);
  const engagementRef = useRef(null);
  const revealObserver = useRef(null);
  const stepInterval = useRef(null);
  const totalSteps = 5;

  const [form, setForm] = useState({
    full_name: '', email: '', company_name: '', phone_number: '', agent_count: '', enrollment_focus: '',
    how_heard: '', how_heard_detail: '',
  });

  // Load Bootstrap Icons
  useEffect(() => {
    if (!document.querySelector('link[href*="bootstrap-icons"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css';
      document.head.appendChild(link);
    }
    document.body.style.background = '#0b0b0b';
    return () => { document.body.style.background = ''; };
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Step cycling
  useEffect(() => {
    stepInterval.current = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(stepInterval.current);
  }, []);

  // Scroll reveal
  useEffect(() => {
    revealObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            revealObserver.current.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    const els = document.querySelectorAll("[data-reveal]");
    els.forEach(el => revealObserver.current.observe(el));
    return () => { if (revealObserver.current) revealObserver.current.disconnect(); };
  }, []);

  // Metrics counter
  useEffect(() => {
    if (!engagementRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !metricsCounted.current) {
          metricsCounted.current = true;
          const targets = { engagement: 3.2, savings: 80, states: 50 };
          const duration = 2000;
          const start = performance.now();
          const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const ease = 1 - Math.pow(1 - progress, 3);
            setMetricValues({
              engagement: parseFloat((targets.engagement * ease).toFixed(1)),
              savings: Math.round(targets.savings * ease),
              states: Math.round(targets.states * ease)
            });
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(engagementRef.current);
    return () => observer.disconnect();
  }, []);

  // Calendly embed loader
  useEffect(() => {
    if (currentStep !== 5) return;
    let scriptEl = null;
    // Every branch that ends WITHOUT a rendered widget has to come through here.
    // A console.warn is invisible to the partner sitting on the final step.
    const failEmbed = (reason, err) => {
      console.warn(`Calendly embed ${reason}:`, err);
      setCalendlyFailed(true);
    };
    setCalendlyFailed(false);
    const existingEl = document.querySelector('script[src*="calendly"]');
    try {
      if (!existingEl) {
        scriptEl = document.createElement('script');
        scriptEl.src = 'https://assets.calendly.com/assets/external/widget.js';
        scriptEl.async = true;
        document.head.appendChild(scriptEl);
      }
      const initEmbed = () => {
        try {
          const container = document.getElementById('calendly-embed');
          if (container && window.Calendly) {
            container.innerHTML = '';
            const prefillName = form.full_name || '';
            const prefillEmail = form.email || '';
            const urlParams = '?hide_landing_page_details=1&hide_gdpr_banner=1&background_color=ffffff&text_color=2d2d2d&primary_color=f97316'
              + (prefillName ? `&name=${encodeURIComponent(prefillName)}` : '')
              + (prefillEmail ? `&email=${encodeURIComponent(prefillEmail)}` : '');
            window.Calendly.initInlineWidget({
              url: CALENDLY_URL + urlParams,
              parentElement: container,
              prefill: {
                name: prefillName,
                email: prefillEmail,
              },
            });
          } else {
            // An ad-blocker can answer widget.js with an empty 200: the script
            // "loads", window.Calendly never appears, and this branch used to
            // fall through silently, leaving an empty 550px box.
            failEmbed('script loaded without window.Calendly', null);
          }
        } catch (err) {
          failEmbed('config failed', err);
        }
      };
      if (window.Calendly) initEmbed();
      else {
        const pendingEl = scriptEl || existingEl;
        // onerror was never wired, so a blocked or failed widget.js reached no
        // catch at all -- not even the console.warn.
        if (pendingEl) {
          pendingEl.onload = initEmbed;
          pendingEl.onerror = (err) => failEmbed('script failed to load', err);
        } else {
          failEmbed('no script element to await', null);
        }
      }
    } catch (e) {
      failEmbed('failed to load', e);
    }
    return () => {
      if (scriptEl?.parentNode) scriptEl.parentNode.removeChild(scriptEl);
      const el = document.getElementById('calendly-embed');
      if (el) el.innerHTML = '';
    };
  }, [currentStep, form.full_name, form.email]);

  const openModal = () => { setShowModal(true); document.documentElement.style.overflow = 'hidden'; };
  const closeModal = () => {
    setShowModal(false);
    document.documentElement.style.overflow = '';
    setTimeout(() => {
      setCurrentStep(1);
      if (submitted) { setSubmitted(false); setForm({ full_name: '', email: '', company_name: '', phone_number: '', agent_count: '', enrollment_focus: '', how_heard: '', how_heard_detail: '' }); }
    }, 300);
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const onPhoneInput = (e) => {
    let raw = e.target.value.replace(/\D/g, '');
    if (raw.length === 11 && raw.startsWith('1')) raw = raw.slice(1);
    raw = raw.slice(0, 10);
    setForm(prev => ({ ...prev, phone_number: formatPhone(raw) }));
    if (errors.phone_number) setErrors(prev => { const c = { ...prev }; delete c.phone_number; return c; });
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => phone.replace(/\D/g, '').length === 10;

  const validateStep1 = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = 'Full name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(form.email.trim())) errs.email = 'Enter a valid email address';
    if (!form.company_name.trim()) errs.company_name = 'Company name is required';
    if (!form.phone_number.trim()) errs.phone_number = 'Phone number is required';
    else if (!isValidPhone(form.phone_number)) errs.phone_number = 'Enter a valid 10-digit phone number';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const clearError = (field) => {
    if (errors[field]) setErrors(prev => { const c = { ...prev }; delete c[field]; return c; });
  };

  const canProceed = (() => {
    switch (currentStep) {
      case 1: return form.full_name.trim() && form.email.trim() && form.company_name.trim() && form.phone_number.trim();
      case 2: return form.agent_count !== '';
      case 3: return form.enrollment_focus !== '';
      case 4:
        if (!form.how_heard) return false;
        if (form.how_heard === 'representative') return form.how_heard_detail !== '';
        if (form.how_heard === 'referral') return form.how_heard_detail.trim() !== '';
        return true;
      default: return false;
    }
  })();

  const progressPercent = (currentStep / totalSteps) * 100;

  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep < totalSteps && canProceed) setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => { if (currentStep > 1) setCurrentStep(prev => prev - 1); };

  const submitForm = async () => {
    if (!canProceed) return;
    setSubmitting(true);
    setSubmitError(null);
    const nameParts = form.full_name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || nameParts[0] || '';
    try {
      const res = await fetch('/api/forms/partners', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName, last_name: lastName, email: form.email.trim(),
          company_name: form.company_name, phone_number: form.phone_number.replace(/\D/g, ''),
          agent_count: form.agent_count, enrollment_focus: form.enrollment_focus,
          how_heard: form.how_heard, how_heard_detail: form.how_heard_detail,
        })
      });
      // `fetch` resolves on 400/429/500 -- it rejects only on a network fault,
      // so without this check a refused application renders the success screen.
      if (res.ok) {
        setSubmitted(true); setCurrentStep(5);
      } else {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data?.error || 'Failed to submit application. Please try again.');
      }
    } catch (e) {
      console.error('Submit failed:', e);
      setSubmitError('Could not reach the server. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  const StepIcon = ({ type }) => {
    if (type === 'clipboard') return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="M9 14l2 2 4-4"/></svg>;
    if (type === 'call') return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94"/><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
    if (type === 'rocket') return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 3 0 3 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-3 0-3"/></svg>;
    return null;
  };

  return (
    <div className="partners-page">
      {/* Navigation */}
      <nav className={`partners-nav ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-inner">
          <Link to="/" className="brand">
            <img loading="lazy" src="/oval-logo.webp" alt="Oval" className="brand-logo" style={scrolled ? {} : { filter: 'brightness(0) invert(1)' }} />
          </Link>
          <div className="nav-actions">
            <Link to="/login" className="nav-user-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </Link>
            <button className="nav-cta-btn" onClick={openModal}>Apply Now</button>
            <button className="hamburger" onClick={() => { setMobileMenuOpen(!mobileMenuOpen); document.documentElement.style.overflow = mobileMenuOpen ? '' : 'hidden'; }}>
              <span className="line"/><span className="line"/><span className="line"/>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="mobile-menu" onClick={(e) => { if (e.target === e.currentTarget) { setMobileMenuOpen(false); document.documentElement.style.overflow = ''; } }}>
            <div className="mobile-menu-inner">
              <button className="mobile-btn" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>Partner Login</button>
              <button className="mobile-btn primary" onClick={() => { openModal(); setMobileMenuOpen(false); }}>Apply Now</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-media"><img loading="lazy" src={HERO_IMG} alt="" className="hero-img"/><div className="hero-overlay"/></div>
        <div className="hero-content">
          <div className="hero-inner data-reveal-el">
            <div className="hero-badge"><span className="badge-dot"/>Now Accepting Partners</div>
            <h1 className="hero-headline">Turn your plan into a lifestyle benefit <span className="accent">your members actually engage with.</span></h1>
            <p className="hero-sub">Oval provides coverage to prescriptions that traditional insurance doesn't cover—Like GLP-1s (Ozempic, Monjaro), Testosterone, Peptides, Hair Loss, ED and many more specialty prescriptions.</p>
            <div className="hero-ctas">
              <button className="btn-oval" onClick={openModal}><span>Become a Partner</span><i className="bi bi-arrow-right"/></button>
              <a href="#how-it-works" className="see-more-link">How It Works <i className="bi bi-chevron-down"/></a>
            </div>
          </div>
        </div>
      </section>

      {/* The Offer */}
      <section className="offer-section">
        <div className="container-wrap">
          <div className="offer-content data-reveal-el">
            <span className="label-tag">The Offer</span>
            <h2 className="offer-headline">A Telehealth platform driving engagement <span className="accent">that translates to retention</span></h2>
            <p className="offer-sub">From weight management to mental wellness, your members get access to medications that are normally out-of-pocket expenses—at a fraction of the cost.</p>
          </div>
          <div className="marquee-wrap data-reveal-el">
            <div className="marquee-track">
              <div className="marquee-content">
                {offerCategories.map((cat, i) => <div key={`a-${i}`} className="offer-pill"><i className={`bi ${cat.icon}`}/><span>{cat.label}</span></div>)}
              </div>
              <div className="marquee-content" aria-hidden="true">
                {offerCategories.map((cat, i) => <div key={`b-${i}`} className="offer-pill"><i className={`bi ${cat.icon}`}/><span>{cat.label}</span></div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement = Retention */}
      <section className="engagement-section" ref={engagementRef}>
        <div className="container-wrap">
          <div className="engagement-grid">
            <div className="engagement-left data-reveal-el">
              <span className="label-tag">The Insight</span>
              <h2 className="section-title">Member Engagement <span className="accent">= Member Retention</span></h2>
              <p className="section-desc">When members use their benefits, they stay. Oval transforms passive health plans into active daily tools—telehealth visits, medication management, and wellness check-ins members actually come back to.</p>
            </div>
            <div className="engagement-right data-reveal-el">
              <div className="insight-card insight-card-main">
                <div className="insight-card-header"><div className="insight-live"><span className="insight-live-dot"/><span>Live</span></div><span className="insight-period">30-day trend</span></div>
                <div className="insight-hero-metric">
                  <span className="insight-hero-value">{metricValues.engagement.toFixed(1)}</span>
                  <span className="insight-hero-suffix">x</span>
                  <span className="insight-hero-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg></span>
                </div>
                <span className="insight-hero-label">Higher engagement vs. traditional telehealth</span>
                <div className="insight-chart">
                  <svg viewBox="0 0 280 80" preserveAspectRatio="none" className="insight-svg">
                    <defs><linearGradient id="insightGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF7A00" stopOpacity="0.15"/><stop offset="100%" stopColor="#FF7A00" stopOpacity="0"/></linearGradient></defs>
                    <path className="insight-area" d="M0,72 C30,68 50,60 80,52 C110,44 130,50 160,38 C190,26 210,30 240,18 C255,12 270,8 280,6 L280,80 L0,80 Z"/>
                    <path className="insight-line" d="M0,72 C30,68 50,60 80,52 C110,44 130,50 160,38 C190,26 210,30 240,18 C255,12 270,8 280,6"/>
                    <circle className="insight-dot" cx="280" cy="6" r="3.5"/><circle className="insight-dot-pulse" cx="280" cy="6" r="3.5"/>
                  </svg>
                </div>
              </div>
              <div className="insight-stats-row">
                <div className="insight-card insight-card-stat">
                  <div className="insight-stat-icon insight-stat-icon-savings"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div>
                  <div className="insight-stat-content"><div className="insight-stat-value-row"><span className="insight-stat-value">{metricValues.savings}</span><span className="insight-stat-suffix">%</span></div><span className="insight-stat-label">Rx savings for members</span></div>
                </div>
                <div className="insight-card insight-card-stat">
                  <div className="insight-stat-icon insight-stat-icon-states"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg></div>
                  <div className="insight-stat-content"><div className="insight-stat-value-row"><span className="insight-stat-value">{metricValues.states}</span><span className="insight-stat-suffix-sm">states</span></div><span className="insight-stat-label">Licensed nationwide</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="vision-section">
        <div className="container-wrap">
          <div className="vision-header data-reveal-el">
            <span className="label-tag light">The Vision</span>
            <h2 className="vision-title">We're switching from healthcare as crisis management <span className="gradient-accent">to healthcare as consumer utility</span></h2>
            <p className="vision-sub">Your members don't just need insurance for emergencies. They need daily access to care that fits their life—prescriptions, providers, and wellness tools that work like the consumer apps they already love.</p>
          </div>
          <div className="pillars-grid data-reveal-el">
            {pillars.map((p, i) => (
              <div key={i} className="pillar-card" style={{ transitionDelay: `${i * 0.12}s` }}>
                <div className="pillar-icon"><i className={`bi ${p.icon}`}/></div>
                <h3 className="pillar-title">{p.title}</h3>
                <p className="pillar-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Break */}
      <section className="quote-break data-reveal-el">
        <div className="quote-glow"/>
        <div className="quote-inner">
          <div className="quote-mark-wrap"><span className="quote-mark">&ldquo;</span></div>
          <p className="quote-text">
            {quoteWords.map((word, i) => (
              <span key={i} className={`quote-word ${[7,8,9].includes(i) ? "quote-accent" : ""}`} style={{ '--i': i }}>{word} </span>
            ))}
          </p>
          <div className="quote-attribution"><span className="quote-line"/><span className="quote-source">The Oval Philosophy</span></div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="process-section">
        <div className="container-wrap">
          <div className="process-header data-reveal-el">
            <span className="label-tag">Simple Onboarding</span>
            <h2 className="section-title">From Application to Live<br/>in Under 7 Days</h2>
          </div>
          <div className="timeline-track data-reveal-el">
            <div className="timeline-connector"><div className="timeline-connector-fill" style={{ width: `${activeStep * 50}%` }}/></div>
            <div className="timeline-nodes">
              {steps.map((s, i) => (
                <div key={i} className={`timeline-node ${activeStep === i ? "node-active" : ""} ${activeStep > i ? "node-done" : ""}`}>
                  <div className="node-dot">
                    {activeStep > i ? <svg className="node-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : <span className="node-num">{i + 1}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="steps-row data-reveal-el">
            {steps.map((s, i) => (
              <div key={i} className={`step-card ${activeStep === i ? "step-active" : ""} ${activeStep > i ? "step-done" : ""}`} style={{ transitionDelay: `${i * 0.12}s` }}>
                <div className="step-icon-wrap"><StepIcon type={s.icon}/><div className="step-icon-ring"/></div>
                <div className="step-content"><span className="step-num">{s.num}</span><h3 className="step-title">{s.title}</h3><p className="step-desc">{s.desc}</p></div>
                <div className="step-progress"><div className="step-progress-fill"/></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="plans-section">
        <div className="container-wrap">
          <div className="plans-header data-reveal-el">
            <span className="label-tag">What Members Get</span>
            <h2 className="section-title">Three Tiers, Complete Coverage</h2>
            <p className="plans-sub">Flexible membership options to serve individuals, families, and enterprise groups.</p>
          </div>
          <div className="plans-grid data-reveal-el">
            <div className="plan-card">
              <div className="plan-tier">Entry</div>
              <h3 className="plan-name">Oval Access</h3>
              <p className="plan-desc">Essential telehealth and pharmacy access.</p>
              <ul className="plan-features"><li><i className="bi bi-check2"/>Telehealth visits</li><li><i className="bi bi-check2"/>Patient assistance program</li><li><i className="bi bi-check2"/>GLP-1 consultations</li><li><i className="bi bi-check2"/>24/7 support</li></ul>
            </div>
            <div className="plan-card featured">
              <div className="popular-tag">Most Popular</div>
              <div className="plan-tier">Premium</div>
              <h3 className="plan-name">Oval Plus</h3>
              <p className="plan-desc">VIP treatment with injectables and concierge care.</p>
              <ul className="plan-features"><li><i className="bi bi-check2"/>Everything in One</li><li><i className="bi bi-check2"/>GLP-1 injections</li><li><i className="bi bi-check2"/>TRT & Peptide prescriptions</li><li><i className="bi bi-check2"/>VIP perks & priority</li></ul>
            </div>
            <div className="plan-card">
              <div className="plan-tier">Core</div>
              <h3 className="plan-name">Oval One</h3>
              <p className="plan-desc">Full medication access with expanded treatments.</p>
              <ul className="plan-features"><li><i className="bi bi-check2"/>Everything in Access</li><li><i className="bi bi-check2"/>ED & hair loss medications</li><li><i className="bi bi-check2"/>Mental health prescriptions</li><li><i className="bi bi-check2"/>Up to 80% Rx savings</li></ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container-wrap">
          <div className="cta-card-wrap data-reveal-el">
            <div className="cta-text">
              <h2 className="cta-title">Ready to offer healthcare your members will actually use?</h2>
              <p className="cta-sub">Apply now. Get approved. Launch in under a week.</p>
            </div>
            <div className="cta-actions">
              <button className="btn-white-oval" onClick={openModal}><span>Apply to Partner</span><i className="bi bi-arrow-right"/></button>
              <p className="cta-note">Free to apply &bull; No commitment &bull; 24hr approval</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="partners-footer data-reveal-el">
        <div className="container-wrap">
          <div className="footer-top">
            <div className="footer-brand">
              <img loading="lazy" src="/oval-logo.webp" alt="Oval" className="footer-logo" style={{ filter: 'brightness(0) invert(1)' }} />
              <p className="footer-tagline">Modern healthcare your members actually use.</p>
            </div>
            <nav className="footer-links">
              <Link to="/terms">Terms & Conditions</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/hipaa">HIPAA Notice</Link>
              <Link to="/states">States We Serve</Link>
            </nav>
            <div className="footer-cert">
              <span className="footer-cert-label">Certified by:</span>
              <a href="https://www.legitscript.com/websites/ovalcare.com" target="_blank" rel="noopener noreferrer"><img loading="lazy" src="/images/legit-script.webp" alt="LegitScript Certified" className="footer-cert-img"/></a>
            </div>
          </div>
          <div className="footer-bottom"><span className="footer-copy">&copy; 2025 Oval Care. All rights reserved.</span></div>
        </div>
      </footer>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal-container">
            <button className="modal-close" onClick={closeModal}><i className="bi bi-x-lg"/></button>
            {currentStep <= 4 ? (
              <div className="modal-form">
                <div className="form-header">
                  <div className="form-badge">Partner Application</div>
                  <h2 className="form-title">Let's Get You Started</h2>
                  <p className="form-subtitle">Takes about 2 minutes. We'll be in touch within 24 hours.</p>
                  <div className="progress-container"><div className="progress-track"><div className="progress-fill" style={{ width: `${progressPercent}%` }}/></div></div>
                </div>
                <div className="form-body">
                  {currentStep === 1 && (
                    <div className="form-step">
                      <h3 className="step-question">Tell us about yourself</h3>
                      <div className="input-stack">
                        <div className={`input-group ${errors.full_name ? "has-error" : ""}`}>
                          <label>Full Name</label>
                          <input value={form.full_name} onChange={e => { setForm(p => ({ ...p, full_name: e.target.value })); clearError('full_name'); }} placeholder="John Smith" />
                          {errors.full_name && <span className="field-error">{errors.full_name}</span>}
                        </div>
                        <div className={`input-group ${errors.email ? "has-error" : ""}`}>
                          <label>Email</label>
                          <input value={form.email} onChange={e => { setForm(p => ({ ...p, email: e.target.value })); clearError('email'); }} placeholder="john@company.com" type="email" />
                          {errors.email && <span className="field-error">{errors.email}</span>}
                        </div>
                        <div className={`input-group ${errors.company_name ? "has-error" : ""}`}>
                          <label>Company / Agency Name</label>
                          <input value={form.company_name} onChange={e => { setForm(p => ({ ...p, company_name: e.target.value })); clearError('company_name'); }} placeholder="Acme Insurance Agency" />
                          {errors.company_name && <span className="field-error">{errors.company_name}</span>}
                        </div>
                        <div className={`input-group ${errors.phone_number ? "has-error" : ""}`}>
                          <label>Phone Number</label>
                          <input value={form.phone_number} onChange={onPhoneInput} placeholder="(555) 123-4567" type="tel" maxLength="14" />
                          {errors.phone_number && <span className="field-error">{errors.phone_number}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                  {currentStep === 2 && (
                    <div className="form-step">
                      <h3 className="step-question">How many agents in your organization?</h3>
                      <div className="choice-row">
                        {agentOptions.map(o => <button key={o.value} type="button" className={`choice-chip ${form.agent_count === o.value ? "selected" : ""}`} onClick={() => setForm(p => ({ ...p, agent_count: o.value }))}>{o.label}</button>)}
                      </div>
                    </div>
                  )}
                  {currentStep === 3 && (
                    <div className="form-step">
                      <h3 className="step-question">What's your enrollment focus?</h3>
                      <div className="choice-row">
                        {enrollmentOptions.map(o => <button key={o.value} type="button" className={`choice-chip ${form.enrollment_focus === o.value ? "selected" : ""}`} onClick={() => setForm(p => ({ ...p, enrollment_focus: o.value }))}>{o.label}</button>)}
                      </div>
                    </div>
                  )}
                  {currentStep === 4 && (
                    <div className="form-step">
                      <h3 className="step-question">How did you hear about us?</h3>
                      <div className="choice-row">
                        {howHeardOptions.map(o => <button key={o.value} type="button" className={`choice-chip ${form.how_heard === o.value ? "selected" : ""}`} onClick={() => setForm(p => ({ ...p, how_heard: o.value, how_heard_detail: '' }))}>{o.label}</button>)}
                      </div>
                      {form.how_heard === 'representative' && (
                        <div className="how-heard-detail">
                          <label className="detail-label">Select representative</label>
                          <select className="detail-select" value={form.how_heard_detail} onChange={e => setForm(p => ({ ...p, how_heard_detail: e.target.value }))}>
                            <option value="">Choose a name...</option>
                            {representativeNames.map(name => <option key={name} value={name}>{name}</option>)}
                          </select>
                        </div>
                      )}
                      {form.how_heard === 'referral' && (
                        <div className="how-heard-detail">
                          <label className="detail-label">Who referred you?</label>
                          <input className="detail-input" value={form.how_heard_detail} onChange={e => setForm(p => ({ ...p, how_heard_detail: e.target.value }))} placeholder="Enter name or company" />
                        </div>
                      )}
                      {submitError && <span className="field-error" role="alert">{submitError}</span>}
                    </div>
                  )}
                </div>
                <div className="form-footer">
                  {currentStep > 1 ? <button className="btn-back" onClick={prevStep}><i className="bi bi-arrow-left"/><span>Back</span></button> : <div/>}
                  {currentStep < 4 ? (
                    <button className="btn-continue" disabled={!canProceed} onClick={nextStep}><span>Continue</span><i className="bi bi-arrow-right"/></button>
                  ) : (
                    <button className="btn-submit" disabled={!canProceed || submitting} onClick={submitForm}>
                      {submitting ? <span><i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }}/> Submitting...</span> : <span>Submit Application <i className="bi bi-check-lg"/></span>}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="modal-form">
                <div className="form-header">
                  <div className="form-badge">Partner Application</div>
                  <h2 className="form-title">Book Your Onboarding Call</h2>
                  <p className="form-subtitle">Pick a time that works for you. We will confirm within 24 hours.</p>
                  <div className="progress-container"><div className="progress-track"><div className="progress-fill" style={{ width: '100%' }}/></div></div>
                </div>
                <div className="form-body">
                  <div className="cal-success-row">
                    <div className="cal-success-icon"><i className="bi bi-check-lg"/></div>
                    <p className="cal-success-text">Thanks {form.full_name.split(' ')[0]}! Your application has been submitted.</p>
                  </div>
                  {calendlyFailed ? (
                    <div className="cal-embed-container" role="alert" style={{ height: 'auto', padding: '20px' }}>
                      <p className="field-error">We could not load the scheduling calendar.</p>
                      <p className="cal-success-text">
                        Your application has been received. Book your onboarding call directly here:{' '}
                        <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">{CALENDLY_URL}</a>
                      </p>
                    </div>
                  ) : (
                    <div id="calendly-embed" className="cal-embed-container" />
                  )}
                </div>
                <div className="form-footer">
                  <div/>
                  <button className="btn-continue" onClick={closeModal}><span>Done</span><i className="bi bi-check-lg"/></button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Fixed CTA */}
      <div className="mobile-fixed-cta"><button className="mobile-fixed-btn" onClick={openModal}>Apply Now <span>&rarr;</span></button></div>
    </div>
  );
};

export default PartnersPage;
